import React, { useEffect, useState, useCallback } from "react";

const API_BASE = "https://backend-coupon-distribution-nhf.onrender.com";

const StatsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/d3-stats`);
      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.error || "Could not load statistics.");
      }

      setStats(data);
      setUpdatedAt(new Date());
    } catch (err) {
      console.error("Error loading d3 stats:", err);
      setError(err.message || "Could not load statistics.");
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const total = stats?.total || 0;
  const adequate = stats?.adequate || 0;
  const inadequate = stats?.inadequate || 0;
  const pct = (n) => (total > 0 ? Math.round((n / total) * 1000) / 10 : 0);

  const styles = `
    .sd-root { font-family: 'Inter', Arial, sans-serif; background: #f8fafc; min-height: 100vh; padding: 20px; color: #0f172a; }
    .sd-container { max-width: 800px; margin: 0 auto; background: white; border-radius: 16px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); padding: 30px; }
    .sd-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 28px; flex-wrap: wrap; }
    .sd-header h1 { font-size: 22px; color: #10b981; margin: 0 0 4px; }
    .sd-header p { color: #64748b; font-size: 13px; margin: 0; }
    .sd-refresh { padding: 10px 18px; font-size: 14px; font-weight: 600; border: none; border-radius: 8px; background: #f1f5f9; color: #475569; cursor: pointer; transition: all 0.2s; }
    .sd-refresh:hover:not(:disabled) { background: #e2e8f0; }
    .sd-refresh:disabled { opacity: 0.6; cursor: not-allowed; }

    .sd-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 28px; }
    @media (max-width: 600px) { .sd-cards { grid-template-columns: 1fr; } }
    .sd-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; background: #f8fafc; }
    .sd-card.good { background: #f0fdf4; border-color: #bbf7d0; }
    .sd-card.bad { background: #fef2f2; border-color: #fecaca; }
    .sd-card-label { font-size: 13px; font-weight: 600; color: #64748b; margin-bottom: 8px; }
    .sd-card-value { font-size: 38px; font-weight: 800; line-height: 1; }
    .sd-card.good .sd-card-value { color: #059669; }
    .sd-card.bad .sd-card-value { color: #dc2626; }
    .sd-card-sub { font-size: 13px; color: #64748b; margin-top: 6px; }

    .sd-section-title { font-size: 15px; font-weight: 700; color: #0f172a; margin-bottom: 12px; }
    .sd-bar { display: flex; height: 22px; border-radius: 999px; overflow: hidden; background: #e2e8f0; margin-bottom: 10px; }
    .sd-bar-green { background: #22c55e; transition: width 0.6s ease-out; }
    .sd-bar-red { background: #ef4444; transition: width 0.6s ease-out; }
    .sd-legend { display: flex; gap: 20px; flex-wrap: wrap; font-size: 13px; color: #475569; margin-bottom: 24px; }
    .sd-legend span { display: inline-flex; align-items: center; gap: 6px; }
    .sd-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }

    .sd-meta { font-size: 12px; color: #94a3b8; line-height: 1.6; border-top: 1px solid #f1f5f9; padding-top: 16px; }
    .sd-msg { text-align: center; padding: 40px 10px; color: #64748b; font-size: 15px; }
    .sd-error { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; border-radius: 8px; padding: 12px 14px; font-size: 14px; font-weight: 600; margin-bottom: 20px; }
  `;

  return (
    <div className="sd-root">
      <style>{styles}</style>
      <div className="sd-container">

        <div className="sd-header">
          <div>
            <h1>📊 Vitamin D Screening Overview</h1>
            <p>Summary of everyone who has completed the questionnaire.</p>
          </div>
          <button className="sd-refresh" onClick={loadStats} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {error && <div className="sd-error">{error}</div>}

        {loading && !stats && (
          <div className="sd-msg">
            Loading statistics... the server may take a few seconds to wake up.
          </div>
        )}

        {stats && (
          <>
            <div className="sd-cards">
              <div className="sd-card">
                <div className="sd-card-label">Total screened</div>
                <div className="sd-card-value">{total}</div>
                <div className="sd-card-sub">Average score {stats.avgScore}</div>
              </div>
              <div className="sd-card good">
                <div className="sd-card-label">Adequate</div>
                <div className="sd-card-value">{adequate}</div>
                <div className="sd-card-sub">{pct(adequate)}% of screened</div>
              </div>
              <div className="sd-card bad">
                <div className="sd-card-label">Inadequate</div>
                <div className="sd-card-value">{inadequate}</div>
                <div className="sd-card-sub">{pct(inadequate)}% of screened</div>
              </div>
            </div>

            <div className="sd-section-title">Adequate vs inadequate</div>
            <div className="sd-bar">
              <div className="sd-bar-green" style={{ width: `${pct(adequate)}%` }} />
              <div className="sd-bar-red" style={{ width: `${pct(inadequate)}%` }} />
            </div>
            <div className="sd-legend">
              <span><i className="sd-dot" style={{ background: "#22c55e" }} /> Adequate (score below {stats.cutoff})</span>
              <span><i className="sd-dot" style={{ background: "#ef4444" }} /> Inadequate (score {stats.cutoff} or above)</span>
            </div>

            <div className="sd-meta">
              {updatedAt && <>Last updated {updatedAt.toLocaleTimeString()}. </>}
              These figures come from the screening score only, not from blood test results.
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StatsDashboard;
